import {ActionList, ActionMenu, StateLabel} from '@primer/react'
import {clsx} from 'clsx'
import {useLayoutEffect, useRef, useState, type ComponentProps, type ReactNode, type RefObject} from 'react'
import {useReturnFocus} from '@github-ui/use-return-focus'
import {STATE_LABEL_DATA, type PullRequestState as PullRequestStateType} from '../types'
import {useIsOverviewEnabled} from '../hooks/use-feature-state'
import {useAvailableStateChangeActions} from './state-change-actions/use-available-state-change-actions'
import {TriangleDownIcon} from '@primer/octicons-react'
import styles from './PullRequestStateLabel.module.css'
import {
  MarkReadyForReviewAction,
  MarkReadyForReviewFailureDialog,
  useMarkReadyForReviewActionMutation,
} from './state-change-actions/MarkReadyForReviewAction'
import {
  ConvertToDraftAction,
  ConvertToDraftFailureDialog,
  useConvertToDraftActionMutation,
} from './state-change-actions/ConvertToDraftAction'
import {CloseAction, CloseFailureDialog, useCloseActionMutation} from './state-change-actions/CloseAction'
import {ReopenAction, ReopenFailureDialog, useReopenActionMutation} from './state-change-actions/ReopenAction'

interface PullRequestStateLabelProps {
  className?: string
  state: PullRequestStateType
  size?: ComponentProps<typeof StateLabel>['size']
  hidden?: boolean
  fallbackFocusRef?: RefObject<HTMLElement | null>
}

export function PullRequestStateLabel(props: PullRequestStateLabelProps) {
  return useIsOverviewEnabled() ? <InteractiveStateLabel {...props} /> : <StateLabelContent {...props} />
}

function StateLabelContent({
  className,
  state,
  size = 'medium',
  hidden = false,
  isMenu = false,
}: PullRequestStateLabelProps & {isMenu?: boolean}) {
  const stateLabel = STATE_LABEL_DATA[state]

  return hidden ? null : (
    <StateLabel className={clsx('flex-self-start', styles.label, className)} status={stateLabel.status} size={size}>
      {stateLabel.description}
      {isMenu && <TriangleDownIcon className="ml-1 mr-n1" size={size === 'small' ? 12 : 16} />}
    </StateLabel>
  )
}

function InteractiveStateLabel({
  className,
  state,
  size = 'medium',
  hidden = false,
  fallbackFocusRef,
}: PullRequestStateLabelProps) {
  // Keep actions mounted when their menu or scroll-dependent trigger disappears so failures remain recoverable.
  const markReadyForReviewMutation = useMarkReadyForReviewActionMutation()
  const convertToDraftMutation = useConvertToDraftActionMutation()
  const closeMutation = useCloseActionMutation()
  const reopenMutation = useReopenActionMutation()

  const stateChangeActions = useAvailableStateChangeActions({
    markReadyForReviewMutation,
    convertToDraftMutation,
    closeMutation,
    reopenMutation,
  })

  const isMenu = stateChangeActions.length > 0
  const isMenuVisible = !hidden && isMenu
  const [wasMenuVisible, setWasMenuVisible] = useState(isMenuVisible)

  useLayoutEffect(() => {
    // Primer must receive the fallback return target before its overlay unmounts.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setWasMenuVisible(isMenuVisible)
  }, [isMenuVisible])

  const stateLabel = STATE_LABEL_DATA[state]

  const label = <StateLabelContent className={className} state={state} size={size} isMenu={isMenu} />

  return (
    <>
      {isMenuVisible || wasMenuVisible ? (
        <StateLabelMenu
          label={label}
          description={stateLabel.description}
          fallbackFocusRef={fallbackFocusRef}
          isMenuVisible={isMenuVisible}
        >
          <ActionList>
            {stateChangeActions.map(name => {
              switch (name) {
                case 'mark-ready':
                  return <MarkReadyForReviewAction key={name} mutation={markReadyForReviewMutation} />
                case 'convert-to-draft':
                  return <ConvertToDraftAction key={name} mutation={convertToDraftMutation} />
                case 'close':
                  return <CloseAction key={name} mutation={closeMutation} />
                case 'reopen':
                  return <ReopenAction key={name} mutation={reopenMutation} />
              }
            })}
          </ActionList>
        </StateLabelMenu>
      ) : !hidden ? (
        label
      ) : null}

      <MarkReadyForReviewFailureDialog mutation={markReadyForReviewMutation} />
      <ConvertToDraftFailureDialog mutation={convertToDraftMutation} />
      <CloseFailureDialog mutation={closeMutation} />
      <ReopenFailureDialog mutation={reopenMutation} />
    </>
  )
}

function StateLabelMenu({
  label,
  description,
  fallbackFocusRef,
  isMenuVisible,
  children,
}: {
  label: ReactNode
  description: string
  fallbackFocusRef?: RefObject<HTMLElement | null>
  isMenuVisible: boolean
  children: ReactNode
}) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const {containerRef} = useReturnFocus(fallbackFocusRef ?? anchorRef)

  // The focused trigger can also disappear after its overlay has already closed.
  useLayoutEffect(() => containerRef(anchorRef.current), [containerRef])

  return (
    <ActionMenu anchorRef={anchorRef}>
      <ActionMenu.Anchor>
        <button type="button" aria-label={`Pull request state: ${description}`} className={styles.anchorWrapper}>
          {label}
        </button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay returnFocusRef={isMenuVisible ? anchorRef : (fallbackFocusRef ?? anchorRef)}>
        {children}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
